import { useState, useCallback, useMemo, useRef, Fragment } from 'react';
import { useIntl } from 'react-intl';
import { Button, MenuTrigger, Popover } from 'react-aria-components';
import { IconChevronRightOutlined } from '@repo/monochrome-icons';
import dayjs from 'dayjs';
import { FilterButton, Button as DSKButton } from '@repo/design-system-kit';
import cx from 'clsx';
import { PERIOD_KEYS, type Period } from '../../period-selection';
import { CustomPeriod } from './custom-period/custom-period';
import styles from './period-selector.module.css';

interface PeriodSelectorProps {
  onChangePeriod: (period: Period) => void;
  selectorLabel: string;
  selectedPeriod: Period | null;
  cancelPeriodSelection: () => void;
  maxDate?: string;
  popoverPlacement?: 'bottom right' | 'bottom left';
  isFilterButton?: boolean;
  customPeriodClassName?: string;
  customDropdownClassName?: string;
  customPeriodOptions?: Period[];
  transactionsEntry?: boolean;
  allowSameDate?: boolean;
}

const dateFormatter = new Intl.DateTimeFormat(undefined, {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

export function PeriodSelector({
  onChangePeriod,
  selectorLabel,
  selectedPeriod,
  cancelPeriodSelection,
  maxDate,
  popoverPlacement = 'bottom right',
  isFilterButton = true,
  customPeriodClassName,
  customDropdownClassName,
  customPeriodOptions,
  transactionsEntry = false,
  allowSameDate = false,
}: PeriodSelectorProps): React.ReactNode {
  const [isCustomPeriod, setIsCustomPeriod] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const { formatMessage } = useIntl();
  const wrapper = useRef<HTMLDivElement>(null);

  const defaultPeriodOptions = useMemo(
    () => [
      {
        key: PERIOD_KEYS.THIS_MONTH,
        label: formatMessage({ id: 'shared-kit.period-selector.options.this-month' }),
        startDate: dayjs().startOf('month').format('YYYY-MM-DD'),
        endDate: dayjs().endOf('month').format('YYYY-MM-DD'),
      },
      {
        key: PERIOD_KEYS.LAST_MONTH,
        label: formatMessage({ id: 'shared-kit.period-selector.options.last-month' }),
        startDate: dayjs().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
        endDate: dayjs().subtract(1, 'month').endOf('month').format('YYYY-MM-DD'),
      },
      {
        key: PERIOD_KEYS.CUSTOM_PERIOD,
        label: formatMessage({ id: 'shared-kit.period-selector.options.custom.label' }),
        startDate: null,
        endDate: null,
      },
    ],
    [formatMessage]
  );

  const periodOptions = customPeriodOptions ?? defaultPeriodOptions;

  const isCustomOption = useCallback(
    (key: string | undefined): boolean => key === PERIOD_KEYS.CUSTOM_PERIOD,
    []
  );

  const isCurrentOption = useCallback(
    (key: string): boolean => key === selectedPeriod?.key,
    [selectedPeriod?.key]
  );

  const customPeriodItem = useMemo(
    (): Period | null =>
      isCustomOption(selectedPeriod?.key)
        ? selectedPeriod
        : (periodOptions.find(option => isCustomOption(option.key)) ?? null),
    [selectedPeriod, periodOptions, isCustomOption]
  );

  const handleSelect = (item: Period): void => {
    if (isCustomOption(item.key)) {
      setIsCustomPeriod(true);
    } else {
      onChangePeriod(item);
      setIsOpen(false);
    }
  };

  const handleCancel = useCallback(
    (e: React.MouseEvent): void => {
      e.stopPropagation();
      cancelPeriodSelection();
    },
    [cancelPeriodSelection]
  );

  const handleBack = useCallback((): void => {
    setIsCustomPeriod(false);
  }, []);

  const updateCustomPeriod = ({
    startDate,
    endDate,
  }: {
    startDate: string;
    endDate: string;
  }): void => {
    const item = { ...customPeriodItem, startDate, endDate };
    onChangePeriod(item as Period);
    setIsOpen(false);
  };

  const handleOpenChange = (open: boolean): void => {
    setIsOpen(open);
    setIsCustomPeriod(false);
  };

  const displayLabel = useMemo((): string => {
    if (!selectedPeriod) {
      return selectorLabel;
    }

    const isCustom = isCustomOption(selectedPeriod.key);
    const { startDate, endDate } = selectedPeriod;

    if (isCustom && allowSameDate && startDate && endDate) {
      const formattedStart = dateFormatter.format(new Date(startDate));
      const formattedEnd = dateFormatter.format(new Date(endDate));

      if (startDate === endDate) {
        return formattedStart;
      }

      return `${formattedStart} - ${formattedEnd}`;
    }

    return selectedPeriod.label;
  }, [selectedPeriod, selectorLabel, allowSameDate, isCustomOption]);

  return (
    <div className={styles.periodWrapper} data-test-period-selector ref={wrapper}>
      <MenuTrigger isOpen={isOpen} onOpenChange={handleOpenChange}>
        {isFilterButton ? (
          <FilterButton
            data-test-selector-trigger=""
            hasMenu
            isActive={Boolean(selectedPeriod)}
            isOpen={isOpen}
            onClear={handleCancel}
            onPress={() => {
              handleOpenChange(!isOpen);
            }}
          >
            {displayLabel}
          </FilterButton>
        ) : (
          <DSKButton
            className={cx(styles.dropdownButton, styles.dskButton)}
            data-test-selector-dsk-button-trigger=""
            variant="secondary"
          >
            <p>{displayLabel}</p>
            <IconChevronRightOutlined
              className={cx(isOpen && styles.dropdownOpen)}
              data-test-arrow-icon
            />
          </DSKButton>
        )}

        <Popover
          // @ts-expect-error -- ignore the useref type mismatch
          UNSTABLE_portalContainer={wrapper.current}
          className={styles.dropdown}
          id="custom-period-selector-dropdown"
          placement={popoverPlacement}
        >
          {isCustomPeriod ? (
            <CustomPeriod
              allowSameDate={allowSameDate}
              className={
                customPeriodClassName ??
                (transactionsEntry
                  ? styles.transactionsEntryPeriodContainer
                  : styles.invoicesEntryPeriodContainer)
              }
              endDate={customPeriodItem?.endDate}
              handleBack={handleBack}
              maxDate={maxDate}
              onCancel={() => {
                setIsOpen(false);
              }}
              onUpdate={updateCustomPeriod}
              startDate={customPeriodItem?.startDate}
            />
          ) : (
            <div
              className={
                customDropdownClassName ??
                (transactionsEntry
                  ? styles.transactionsEntryContainer
                  : styles.invoicesEntryContainer)
              }
            >
              {periodOptions.map((item, index) => (
                <Fragment key={item.key}>
                  {isCustomOption(item.key) && <div className={styles.divider} />}
                  <Button
                    className={cx(
                      styles.dropdownButton,
                      'overlay',
                      isCurrentOption(item.key) && styles.dropdownButtonCurrent
                    )}
                    data-option-index={index}
                    id={item.key}
                    onPress={() => {
                      handleSelect(item);
                    }}
                  >
                    {item.label}
                    {isCustomOption(item.key) && <IconChevronRightOutlined />}
                  </Button>
                </Fragment>
              ))}
            </div>
          )}
        </Popover>
      </MenuTrigger>
    </div>
  );
}
